import { NewButtonA } from 'components/elements/NewButton'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import { Body2, Headline1, Headline2, Headline3 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled, { css } from 'styled-components'
import BackgroundImage from 'assets/careers/hero.png'
import Stack from 'components/elements/Stack'
import { URLs } from 'lib/constants/URLs'
import { workingAtKnowunityRoute } from 'lib/constants/routes'
import ArrowRight from 'assets/careers/working-at-knowunity/pointing-arrow.svg'
import { localizeNumber } from 'lib/constants/localization'
import { KnowunityNumbers } from 'lib/constants/KnowunityNumbers'
import { Language } from 'interfaces/Language'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 80px 20px 140px;
    position: relative;
    background-color: transparent;
    background-image: url(${BackgroundImage.src});
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: 0px 0px 48px 48px;
    overflow: visible;
    height: 780px;

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 200px;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #0b0b0d 100%);
        z-index: 2;
    }

    @media (max-width: 860px) {
        padding: 40px 16px 80px;
        min-height: 420px;
        height: 680px;
        border-radius: 0px 0px 32px 32px;
    }
`

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    max-width: 800px;
    width: 100%;
    z-index: 3;
    gap: 25px;
    margin: auto;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    .mobile-button-spacer {
        height: 44px;
    }

    @media ${deviceSize.tablet} {
        gap: 30px;
        padding: 0 5%;
    }

    @media (max-width: 860px) {
        gap: 40px;
        width: 90%;
        .desktop-button {
            visibility: hidden;
        }
    }
`

const ButtonContainer = styled.div`
    display: block;
    width: 100%;
    position: relative;
    height: 225px;
    z-index: 10;
    overflow: visible;
    position: absolute;
    bottom: 0px;
    padding-top: 0px;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    .button-container {
        display: flex;
        width: fit-content;
        gap: 10px;
    }
    @media (max-width: 860px) {
        height: 185px;
    }
`

const ButtonWrapper = styled.div<{ isSticky: boolean }>`
    width: 100%;
    justify-content: center;
    z-index: 10;
    position: relative;
    left: 0;
    right: 0;
    padding: 0px 0;
    display: flex;
    transition: transform 0.3s ease-in-out;
    ${({ isSticky }) =>
        isSticky &&
        css`
            visibility: hidden;
        `}
`

const AppButton = styled(NewButtonA)`
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    p {
        font-family: var(--font-inter);
        font-size: 17px;
        font-weight: 650;
        line-height: 20px;
        letter-spacing: 0.3px;
        display: flex;
        align-items: center;
        gap: 10px;
    }
`

const Headline = styled(Headline1)`
    font-size: 40px;
    font-weight: 650;
    line-height: 44px;
    letter-spacing: -0.08px;
    @media (max-width: 860px) {
        font-size: 23px;
        line-height: 28px;
        letter-spacing: -0.2px;
    }
`

const Subtitle = styled(Headline2)`
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0px;
    padding: 0 10%;
    @media (max-width: 860px) {
        padding: 0px;
    }
`

const StatsContainer = styled.div`
    position: relative;
    width: 100%;
    background-color: #0b0b0d;
    padding: 0px;
    z-index: 5;
    @media (max-width: 860px) {
        padding: 5px 0;
    }
`

const BottomHomePageStats = styled.div`
    width: fit-content;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 8px;
    z-index: 10;
    padding: 0 20px;
    padding-bottom: 30px; // TO remove
    @media (max-width: 860px) {
        padding: 0 10px;
        padding-bottom: 20px; // TO remove
        gap: 3px;
    }
`

const StatCard = styled.div`
    width: 250px;
    height: 116px;
    padding: 18px 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: ${theme.colors.backgroundTransparent};
    border-radius: 24px;

    && h3 {
        color: ${theme.colors.white};
        font-family: var(--font-inter);
        font-weight: 650;
        font-size: 36px;
        line-height: 44px;
        letter-spacing: -0.8px;
    }

    && p {
        color: ${theme.colors.grayScaleSecondary};
        font-family: var(--font-inter);
        font-weight: 650;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.4px;
    }

    @media (max-width: 860px) {
        padding: 12px 0px;
        width: 85px;
        border-radius: 12px;
        height: 60px;

        && h3 {
            font-weight: 650;
            font-size: 17px;
            line-height: 20px;
            letter-spacing: -0.4px;
            margin-bottom: 2px;
        }

        && p {
            font-weight: 450;
            font-size: 9px;
            line-height: 12px;
            letter-spacing: 0.6px;
        }
    }
`

interface CareersHeaderProps {
    isSticky: boolean
    containerRef: React.RefObject<HTMLDivElement>
    buttonRef: React.RefObject<HTMLDivElement>
}

const CareersHeader = ({ isSticky, containerRef, buttonRef }: CareersHeaderProps) => {
    return (
        <>
            <Container ref={containerRef}>
                <ContentWrapper>
                    <Stack gutter={20}>
                        <Headline textAlign="center" color={theme.colors.white} fontFamily="var(--font-inter)">
                            {`We're democratizing education for 1 billion students with our AI learning companion — and we're moving at unprecedented speed to achieve this`}
                        </Headline>
                        <Subtitle textAlign="center" color={theme.colors.grayScaleSecondary} fontFamily="var(--font-inter)">
                            Join our elite team building the next-generation AI learning companion that is redefining what is
                            possible in learning
                        </Subtitle>
                    </Stack>
                </ContentWrapper>
                <ButtonContainer>
                    <ButtonWrapper ref={buttonRef} isSticky={isSticky}>
                        <div className="button-container">
                            <AppButton
                                href={URLs.JobsWebsitePersonio}
                                color={theme.colors.white}
                                borderRadius={theme.borderRadius.webappButton}
                                height={44}
                                width={160}
                            >
                                <Body2
                                    color={theme.colors.black}
                                    fontWeight={700}
                                    fontSize={18}
                                    fontFamily="var(--font-inter)"
                                >
                                    {'Open positions'}
                                </Body2>
                            </AppButton>
                            <AppButton
                                href={workingAtKnowunityRoute}
                                color={theme.colors.white}
                                borderRadius={theme.borderRadius.webappButton}
                                height={44}
                                width={160}
                            >
                                <Body2
                                    color={theme.colors.black}
                                    fontWeight={700}
                                    fontSize={18}
                                    fontFamily="var(--font-inter)"
                                >
                                    {'Learn more'}
                                    <ArrowRight />
                                </Body2>
                            </AppButton>
                        </div>
                    </ButtonWrapper>
                </ButtonContainer>
            </Container>
            <StatsContainer>
                <BottomHomePageStats>
                    <StatCard>
                        <Headline3 textAlign="center" color={theme.colors.white} fontWeight={700}>
                            {`${localizeNumber(KnowunityNumbers.downloadMillionCount, Language.BritishEnglish)}M+`}
                        </Headline3>
                        <Body2 textAlign="center" color={theme.colors.grayScaleSecondary}>
                            Users
                        </Body2>
                    </StatCard>

                    <StatCard>
                        <Headline3 textAlign="center" color={theme.colors.white} fontWeight={700}>
                            50+
                        </Headline3>
                        <Body2 textAlign="center" color={theme.colors.grayScaleSecondary}>
                            Employees
                        </Body2>
                    </StatCard>

                    <StatCard>
                        <Headline3 textAlign="center" color={theme.colors.white} fontWeight={700}>
                            1
                        </Headline3>
                        <Body2 textAlign="center" color={theme.colors.grayScaleSecondary}>
                            Office building
                        </Body2>
                    </StatCard>

                    <StatCard>
                        <Headline3 textAlign="center" color={theme.colors.white} fontWeight={700}>
                            20+
                        </Headline3>
                        <Body2 textAlign="center" color={theme.colors.grayScaleSecondary}>
                            Nationalities
                        </Body2>
                    </StatCard>
                </BottomHomePageStats>
            </StatsContainer>
        </>
    )
}

export default CareersHeader
